import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials, companyInfo, pageMetadata } from '../data';
import EventCard from '../components/EventCard';

export default function Home() {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentReview, setCurrentReview] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe) {
      setCurrentReview((prev) => (prev + 1) % testimonials.length);
    } else if (isRightSwipe) {
      setCurrentReview((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  // Fetch events from CMS
  useEffect(() => {
    // Set page title and meta description
    document.title = pageMetadata.home.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', pageMetadata.home.description);

    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', pageMetadata.home.title);

    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', pageMetadata.home.description);

    let ogImg = document.querySelector('meta[property="og:image"]');
    if (!ogImg) {
      ogImg = document.createElement('meta');
      ogImg.setAttribute('property', 'og:image');
      document.head.appendChild(ogImg);
    }
    ogImg.setAttribute('content', pageMetadata.home.ogImage);

    const fetchEvents = async () => {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/events?status=kommende&limit=3');
        if (res.ok) {
          const data = await res.json();
          setUpcomingEvents(data.items || []);
        }
      } catch (e) {
        console.error('Error fetching events:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  return (
    <div className="space-y-24 pb-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32 bg-gradient-to-b from-brand-sand/30 to-brand-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Hero Text */}
            <div className="order-2 lg:order-1 lg:col-span-7 space-y-8 text-left">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=95,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/johanne-eis-logo-sand.webp?t=1789029125264"
                alt="Johanne Eis Logo"
                className="w-full max-w-[180px] sm:max-w-[200px] h-auto object-contain mx-auto object-center lg:mx-0 lg:object-left"
              />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl text-brand-dark leading-tight tracking-wide font-display text-center lg:text-left">
                Mød sjælen – <br />
                <span className="italic text-brand-green font-medium">og skab liv i livet.</span>
              </h1>
              <p className="text-brand-muted text-lg sm:text-xl leading-relaxed font-sans max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
                Det felt jeg har adgang til er bindeled imellem det, som er aktuelt i dit liv og det potentiale, der ligger gemt i dig.
              </p>
              <p className="text-brand-muted/80 text-sm leading-relaxed font-sans max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
                Feltet kommer oftest til udtryk igennem Lyd, Stemme og Bevidst Vibration der går forbi fornuften og arbejder helt ind, hvor vi med vores intellekt ikke kan følge med. Vi er nødt til at lade det være op til noget der er større end os selv.
              </p>
              <div className="flex flex-wrap gap-4 pt-4 justify-center lg:justify-start">
                <Link
                  to="/events"
                  className="bg-brand-green hover:bg-brand-green/90 text-brand-cream px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md hover:shadow-lg"
                >
                  Se kommende events
                </Link>
                <Link
                  to="/kontakt"
                  className="bg-brand-cream hover:bg-brand-sand border border-brand-beige text-brand-dark px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-sm"
                >
                  Kontakt Johanne
                </Link>
              </div>
            </div>

            {/* Hero Image */}
            <div className="order-1 lg:order-2 lg:col-span-5 relative">
              <div className="absolute inset-0 bg-brand-green/5 rounded-3xl transform rotate-3 scale-105" />
              <div className="relative aspect-[3/4] rounded-3xl overflow-hidden shadow-xl border border-brand-sand">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-1080x1920-5-1.webp"
                  alt="Johanne Eis"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Deltag i fællesskabet</span>
          <h2 className="text-4xl sm:text-5xl text-brand-dark tracking-wide">Kommende events & cirkler</h2>
          <p className="text-brand-muted font-sans">
            Oplev nærværende, individuelle og meningsfulde events. Herunder kan du se de næste planlagte begivenheder.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <img 
              src="https://assets.auxodev.dk/assets/9c094138-0bed-486b-b914-79750263377d/tribe-loading.gif" 
              alt="Indlæser..." 
              className="h-10 w-auto"
            />
          </div>
        ) : upcomingEvents.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-8">
            {upcomingEvents.map((event) => (
              <EventCard 
                key={event.id} 
                event={event} 
                onSelect={() => window.location.href = `/events?event=${event.id}`}
              />
            ))}
          </div>
        ) : (
          <div className="bg-brand-sand/30 rounded-2xl p-12 text-center border border-brand-sand max-w-2xl mx-auto space-y-6">
            <p className="text-brand-muted font-sans text-lg">
              Der er lige nu ingen planlagte events – kontakt mig for at arrangere et event.
            </p>
            <Link
              to="/kontakt"
              className="inline-block bg-brand-green hover:bg-brand-green/90 text-brand-cream px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm"
            >
              Kontakt mig
            </Link>
          </div>
        )}
      </section>

      {/* About Teaser Section */}
      <section className="bg-brand-sand/20 py-20 border-y border-brand-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="relative aspect-square rounded-3xl overflow-hidden shadow-lg border border-brand-sand">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-620x440-1-1.webp"
                  alt="Om Johanne Eis"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Mød din vejleder</span>
              <h2 className="text-4xl sm:text-5xl text-brand-dark tracking-wide">Jeg er Johanne Eis</h2>
              <div className="space-y-4 text-brand-muted font-sans leading-relaxed">
                <p>
                  Jeg hedder Johanne og jeg skaber levende liv sammen med mennesker, som er klar til at træde ud af overlevelse og ind i deres fuldkommende, nærende liv.
                </p>
                <p>
                  Så du kan mærke dig rodfæstet og forbundet med livskraften dybt indeni – dér, hvor sjælen får plads og kan høres som livsvejleder, og hvor livet føles sandt, nærende og levende.
                </p>
                <p>
                  Herfra kan du let træde ind i din højeste bevidsthed og skabe det liv, du inderst inde drømmer om. Det er en kærlig invitation til at huske, hvem du i virkeligheden er bag alle dine begrænsninger.
                </p>
              </div>
              <div className="pt-4">
                <Link
                  to="/om-johanne"
                  className="inline-block bg-brand-green hover:bg-brand-green/90 text-brand-cream px-6 py-3 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm"
                >
                  Læs mere om mig
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Facebook Group Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-brand-sand/40 rounded-3xl p-8 sm:p-12 lg:p-16 border border-brand-sand">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 space-y-6 text-left">
              <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Gratis fællesskab</span>
              <h2 className="text-4xl text-brand-dark tracking-wide">Facebookgruppen Levende Liv</h2>
              <div className="space-y-4 text-brand-muted font-sans leading-relaxed">
                <p>
                  Levende Liv er til gratis sessioner, der vækker din sande vibration – den du er, når du træder ud af fortællingerne om dig selv. Ud af overlevelse og ind i et fuldkomment, levende liv.
                </p>
                <p>
                  Her inviteres du ind i livets magiske bevægelse gennem stemme, lyd og tromme, hvor livskraften får lov at træde tydeligere frem og bevæge sig frit i dig.
                </p>
                <p>
                  Intentionen for gruppen er at skabe et kærligt og ærligt rum, hvor vi kommunikerer fra hjertet og mødes i nærvær og sandhed.
                </p>
              </div>
              <div className="pt-4">
                <a
                  href={companyInfo.facebookGroup}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-brand-green hover:bg-brand-green/90 text-brand-cream px-6 py-3.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 shadow-sm"
                >
                  Tilmeld dig gruppen <i className="fa-brands fa-facebook ml-2" />
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-md border border-brand-sand">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/facebook-gruppe-johanne-eis.webp"
                  alt="Facebookgruppe Levende Liv"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Explore Universe Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Gå på opdagelse</span>
          <h2 className="text-4xl text-brand-dark tracking-wide">Udforsk og gå på opdagelse her</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Cirkler */}
          <div className="bg-brand-cream rounded-2xl border border-brand-sand overflow-hidden group hover:shadow-md transition-all duration-300 flex flex-col">
            <div className="aspect-[4/3] overflow-hidden bg-brand-sand relative">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-350x380-3-1.webp"
                alt="Cirkler"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-6 flex flex-col flex-grow text-left">
              <h3 className="font-display text-2xl text-brand-dark mb-3">Cirkler</h3>
              <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6 flex-grow">
                Gennem ceremonier og levende nærvær åbnes et felt, hvor den ægte, sande dig får plads.
              </p>
              <Link
                to="/events"
                className="text-brand-green hover:text-brand-green/80 font-medium text-sm tracking-wide flex items-center gap-2 transition-colors duration-300 mt-auto"
              >
                Udforsk cirkler <i className="fa-light fa-arrow-right" />
              </Link>
            </div>
          </div>

          {/* Magiske Redskaber */}
          <div className="bg-brand-cream rounded-2xl border border-brand-sand overflow-hidden group hover:shadow-md transition-all duration-300 flex flex-col">
            <div className="aspect-[4/3] overflow-hidden bg-brand-sand relative">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-620x440-7-1.webp"
                alt="Magiske Redskaber"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-6 flex flex-col flex-grow text-left">
              <h3 className="font-display text-2xl text-brand-dark mb-3">Magiske Redskaber</h3>
              <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6 flex-grow">
                Alle Magiske Redskaber der skabes fra min hånd, kommer med budskaber og skaber transformation.
              </p>
              <Link
                to="/kontakt"
                className="text-brand-green hover:text-brand-green/80 font-medium text-sm tracking-wide flex items-center gap-2 transition-colors duration-300 mt-auto"
              >
                Bestil redskab <i className="fa-light fa-arrow-right" />
              </Link>
            </div>
          </div>

          {/* 1:1 Sessioner */}
          <div className="bg-brand-cream rounded-2xl border border-brand-sand overflow-hidden group hover:shadow-md transition-all duration-300 flex flex-col">
            <div className="aspect-[4/3] overflow-hidden bg-brand-sand relative">
              <img
                src="https://assets.auxodev.dk/cdn-cgi/image/width=500,quality=80,format=auto/assets/9c094138-0bed-486b-b914-79750263377d/25-johanne-eis-1080x1920-3n.webp"
                alt="1:1 Sessioner"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
            </div>
            <div className="p-6 flex flex-col flex-grow text-left">
              <h3 className="font-display text-2xl text-brand-dark mb-3">1:1 Sessioner</h3>
              <p className="text-brand-muted text-sm font-sans leading-relaxed mb-6 flex-grow">
                Individuelle sessioner – Skab liv i livet. I en 1:1 session stiller jeg mig til rådighed for det, du har på hjertet.
              </p>
              <Link
                to="/sessioner"
                className="text-brand-green hover:text-brand-green/80 font-medium text-sm tracking-wide flex items-center gap-2 transition-colors duration-300 mt-auto"
              >
                Læs om sessioner <i className="fa-light fa-arrow-right" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="bg-brand-sand/10 py-20 border-y border-brand-sand">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans">Erfaringer</span>
            <h2 className="text-4xl text-brand-dark tracking-wide">Kunderne siger</h2>
          </div>

          {/* Testimonials Slideshow (all screens) */}
          <div className="relative max-w-3xl mx-auto">
            <div 
              className="bg-brand-cream p-6 sm:p-8 md:p-10 rounded-2xl border border-brand-sand flex flex-col justify-between min-h-[320px] text-left transition-all duration-300 ease-in-out"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              <div className="flex-grow flex flex-col justify-center" key={currentReview}>
                <p className="text-brand-muted text-sm sm:text-base md:text-lg leading-relaxed italic mb-6 font-sans animate-fade-in">
                  “ {testimonials[currentReview].text} ”
                </p>
              </div>
              <div className="flex items-center gap-3 pt-4 border-t border-brand-sand/50">
                <span className="font-display text-base font-semibold text-brand-dark">{testimonials[currentReview].name}</span>
              </div>
            </div>

            {/* Navigation & Dots */}
            <div className="flex items-center justify-between mt-6 px-2">
              <button
                onClick={() => setCurrentReview((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="w-10 h-10 rounded-full border border-brand-sand flex items-center justify-center text-brand-dark hover:bg-brand-sand/50 active:bg-brand-sand transition-colors"
                aria-label="Forrige anmeldelse"
              >
                <i className="fa-light fa-chevron-left" />
              </button>

              <div className="flex gap-1.5 max-w-[60%] overflow-x-auto py-1 no-scrollbar justify-center">
                {testimonials.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentReview(idx)}
                    className={`h-2 rounded-full transition-all duration-300 shrink-0 ${
                      currentReview === idx ? 'w-5 bg-brand-green' : 'w-2 bg-brand-sand hover:bg-brand-beige'
                    }`}
                    aria-label={`Gå til udtalelse ${idx + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={() => setCurrentReview((prev) => (prev + 1) % testimonials.length)}
                className="w-10 h-10 rounded-full border border-brand-sand flex items-center justify-center text-brand-dark hover:bg-brand-sand/50 active:bg-brand-sand transition-colors"
                aria-label="Næste anmeldelse"
              >
                <i className="fa-light fa-chevron-right" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-brand-sand/40 rounded-3xl p-8 sm:p-12 lg:p-16 border border-brand-sand text-center space-y-8 relative overflow-hidden">
          <div className="absolute -right-20 -top-20 w-60 h-60 bg-brand-green/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-20 -bottom-20 w-60 h-60 bg-brand-rose/5 rounded-full blur-3xl pointer-events-none" />

          <span className="text-brand-green text-xs font-semibold tracking-widest uppercase font-sans relative z-10">Tag det næste skridt</span>
          <h2 className="text-4xl sm:text-5xl text-brand-dark tracking-wide max-w-3xl mx-auto leading-tight relative z-10">
            Er du klar til at træde nærmere dig selv og lade dit lys skinne?
          </h2>
          <p className="text-brand-muted font-sans text-base sm:text-lg max-w-2xl mx-auto leading-relaxed relative z-10">
            Hvis du mærker et indre kald og en nysgerrighed, er du hjertelig velkommen til at tage kontakt. Jeg skaber kærlige rum, hvor du kan lande i nærvær og åbne for ægte kontakt med den, du i virkeligheden er – dig i dit lys.
          </p>
          <div className="pt-4 relative z-10">
            <Link
              to="/kontakt"
              className="bg-brand-green hover:bg-brand-green/90 text-brand-cream px-8 py-4 rounded-full text-base font-medium tracking-wide transition-all duration-300 shadow-md hover:shadow-lg"
            >
              Skriv til mig
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
